import { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, CheckCheck } from 'lucide-react';
import {
  getNotifications, markNotificationsRead,
  type NotificationItem,
} from '../api/dashboard';

function actionLabel(action: string): string {
  const map: Record<string, string> = {
    'case.submit': 'Case submitted',
    'step.approve': 'Step approved',
    'step.return': 'Case returned',
    'step.escalate': 'Case escalated',
    'determination.approve': 'Determination: approve',
    'determination.escalate': 'Determination: escalate',
    'determination.reject': 'Determination: reject',
    'case.assign': 'Case assigned',
    'case.update': 'Case updated',
  };
  return map[action] || action;
}

function timeAgo(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export default function NotificationBell() {
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const feed = await getNotifications();
      setItems(feed.items);
      setUnread(feed.unread_count);
    } catch {
      // non-fatal; bell stays silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 60000);
    return () => clearInterval(interval);
  }, [load]);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const handleMarkRead = async () => {
    try {
      await markNotificationsRead();
      setUnread(0);
    } catch {
      // non-fatal
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => { setOpen(!open); if (!open) load(); }}
        className="relative p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors"
        aria-label="Notifications"
        title="Notifications"
      >
        <Bell size={18} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white border border-slate-200 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
          <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
            <span className="text-sm font-semibold text-slate-700">Notifications</span>
            {unread > 0 && (
              <button
                onClick={handleMarkRead}
                className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700"
              >
                <CheckCheck size={14} /> Mark read
              </button>
            )}
          </div>
          {loading ? (
            <div className="px-3 py-6 text-center text-sm text-slate-400">Loading...</div>
          ) : items.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-slate-400">No recent activity.</div>
          ) : (
            <ul className="divide-y divide-slate-50">
              {items.slice(0, 20).map((n) => (
                <li key={n.id} className="px-3 py-2 hover:bg-slate-50">
                  <div className="flex items-center justify-between">
                    <a
                      href={n.entity_id ? `#/cases/${n.entity_id}` : undefined}
                      className="text-sm text-slate-700 hover:text-primary-600"
                    >
                      {actionLabel(n.action)}
                    </a>
                    <span className="text-xs text-slate-400">{timeAgo(n.timestamp)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}